import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tab,
  Tabs,
  Paper,
  Alert,
  AlertTitle,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  ButtonGroup,
  Fade,
  Collapse,
  Badge,
  Fab,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Psychology as PsychologyIcon,
  LocalHospital as LocalHospitalIcon,
  Healing as HealingIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import ChatBot from '../components/ChatBot';
import { useAuth } from '../context/AuthContext';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function SearchResultItem({ item, searchTerm, onNavigate }) {
  const highlightText = (text, term) => {
    if (!term) return text;
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#FFE082', fontWeight: 'bold' }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <Card sx={{ mb: 2, cursor: 'pointer' }} onClick={onNavigate}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Chip 
            label={item.category} 
            size="small" 
            color="primary" 
            sx={{ mr: 1 }}
          />
          <Typography variant="h6" component="h3">
            {highlightText(item.title, searchTerm)}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {highlightText(item.content, searchTerm)}
        </Typography>
        {item.subcategory && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {item.subcategory}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function AlzheimerKnowledgeBase() {
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showSearch, setShowSearch] = useState(false);
  const [chatBotOpen, setChatBotOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSelectedCategory('all');
  };

  const navigateToContent = (category) => {
    const categoryMap = {
      'Overview': 0,
      'Diagnosis': 1,
      'Treatment': 2
    };
    setTabValue(categoryMap[category] || 0);
    setShowSearch(false);
  };

  const overviewData = {
    definition: "Alzheimer's disease is a progressive neurodegenerative disorder that primarily affects memory, thinking, and behavior. It is the most common cause of dementia, accounting for 60-80% of dementia cases.",
    statistics: [
      "Affects over 6 million Americans",
      "6th leading cause of death in the US",
      "More common in women than men",
      "Risk increases with age, especially after 65",
    ],
    stages: [
      {
        name: "Preclinical",
        description: "Brain changes occur but no symptoms are apparent",
        duration: "10-20 years before symptoms"
      },
      {
        name: "Mild Cognitive Impairment",
        description: "Slight cognitive decline, may notice memory issues",
        duration: "2-4 years"
      },
      {
        name: "Mild Dementia",
        description: "Memory loss and confusion become noticeable",
        duration: "2-4 years"
      },
      {
        name: "Moderate Dementia",
        description: "Increased confusion, behavior changes, need for care",
        duration: "2-10 years"
      },
      {
        name: "Severe Dementia",
        description: "Complete dependence on others for daily activities",
        duration: "1-3 years"
      }
    ]
  };

  const diagnosisData = {
    earlySymptoms: [
      "Memory loss that disrupts daily life",
      "Challenges in planning or solving problems",
      "Difficulty completing familiar tasks",
      "Confusion with time or place",
      "Trouble understanding visual images",
      "New problems with words in speaking or writing",
      "Misplacing things and losing ability to retrace steps",
      "Decreased or poor judgment",
      "Withdrawal from work or social activities",
      "Changes in mood and personality"
    ],
    diagnosticTests: [
      {
        name: "Cognitive Assessment",
        description: "Tests like MMSE, MoCA to evaluate memory, attention, and language",
        accuracy: "70-80%"
      },
      {
        name: "Brain Imaging",
        description: "MRI, CT, PET scans to look for brain changes",
        accuracy: "85-90%"
      },
      {
        name: "Blood Tests",
        description: "Check for other conditions and biomarkers",
        accuracy: "60-70%"
      },
      {
        name: "Neuropsychological Testing",
        description: "Comprehensive evaluation of cognitive functions",
        accuracy: "80-85%"
      },
      {
        name: "Cerebrospinal Fluid Analysis",
        description: "Measures tau and amyloid proteins",
        accuracy: "85-95%"
      }
    ],
    riskFactors: [
      { factor: "Age", risk: "High", description: "Risk doubles every 5 years after 65" },
      { factor: "Family History", risk: "Medium", description: "Genetic predisposition increases risk" },
      { factor: "Education Level", risk: "Low", description: "Higher education may be protective" },
      { factor: "Cardiovascular Disease", risk: "Medium", description: "Heart health affects brain health" },
      { factor: "Head Trauma", risk: "Medium", description: "Severe head injuries increase risk" },
      { factor: "Lifestyle Factors", risk: "Variable", description: "Diet, exercise, and social engagement" }
    ]
  };

  const treatmentData = {
    medications: [
      {
        name: "Cholinesterase Inhibitors",
        drugs: ["Donepezil (Aricept)", "Rivastigmine (Exelon)", "Galantamine (Razadyne)"],
        stage: "Mild to Moderate",
        effectiveness: "May slow progression for 6-12 months",
        sideEffects: "Nausea, diarrhea, loss of appetite"
      },
      {
        name: "NMDA Receptor Antagonist",
        drugs: ["Memantine (Namenda)"],
        stage: "Moderate to Severe",
        effectiveness: "May help with daily activities",
        sideEffects: "Headache, dizziness, confusion"
      },
      {
        name: "Combination Therapy",
        drugs: ["Donepezil + Memantine (Namzaric)"],
        stage: "Moderate to Severe",
        effectiveness: "May provide additional benefits",
        sideEffects: "Combined side effects of both drugs"
      },
      {
        name: "Aducanumab",
        drugs: ["Aduhelm"],
        stage: "Mild Cognitive Impairment",
        effectiveness: "Controversial, limited evidence",
        sideEffects: "Brain swelling, bleeding"
      }
    ],
    nonPharmacological: [
      {
        category: "Cognitive Interventions",
        approaches: [
          "Cognitive stimulation therapy",
          "Reality orientation",
          "Reminiscence therapy",
          "Cognitive rehabilitation"
        ]
      },
      {
        category: "Physical Interventions",
        approaches: [
          "Regular exercise programs",
          "Physical therapy",
          "Occupational therapy",
          "Dance/movement therapy"
        ]
      },
      {
        category: "Psychosocial Interventions",
        approaches: [
          "Support groups",
          "Behavioral therapy",
          "Music therapy",
          "Pet therapy"
        ]
      },
      {
        category: "Environmental Modifications",
        approaches: [
          "Home safety modifications",
          "Structured routines",
          "Simplified environments",
          "Assistive technologies"
        ]
      }
    ],
    caregiving: [
      "Establish daily routines",
      "Create a safe environment",
      "Maintain social connections",
      "Provide emotional support",
      "Seek respite care",
      "Join support groups",
      "Plan for the future",
      "Take care of caregiver health"
    ]
  };

  // Create searchable content
  const searchableContent = useMemo(() => {
    const content = [];
    
    // Overview content
    content.push({
      id: 'overview-definition',
      title: 'What is Alzheimer\'s Disease?',
      content: overviewData.definition,
      category: 'Overview',
      subcategory: 'Definition'
    });
    
    overviewData.statistics.forEach((stat, index) => {
      content.push({
        id: `overview-stat-${index}`,
        title: 'Alzheimer\'s Statistics',
        content: stat,
        category: 'Overview',
        subcategory: 'Statistics'
      });
    });
    
    overviewData.stages.forEach((stage, index) => {
      content.push({
        id: `overview-stage-${index}`,
        title: `${stage.name} Stage`,
        content: `${stage.description}. Duration: ${stage.duration}`,
        category: 'Overview',
        subcategory: 'Disease Stages'
      });
    });
    
    // Diagnosis content
    diagnosisData.earlySymptoms.forEach((symptom, index) => {
      content.push({
        id: `diagnosis-symptom-${index}`,
        title: 'Early Warning Signs',
        content: symptom,
        category: 'Diagnosis',
        subcategory: 'Symptoms'
      });
    });
    
    diagnosisData.diagnosticTests.forEach((test, index) => {
      content.push({
        id: `diagnosis-test-${index}`,
        title: test.name,
        content: `${test.description}. Accuracy: ${test.accuracy}`,
        category: 'Diagnosis',
        subcategory: 'Diagnostic Tests'
      });
    });
    
    diagnosisData.riskFactors.forEach((risk, index) => {
      content.push({
        id: `diagnosis-risk-${index}`,
        title: `Risk Factor: ${risk.factor}`,
        content: `${risk.description}. Risk Level: ${risk.risk}`,
        category: 'Diagnosis',
        subcategory: 'Risk Factors'
      });
    });
    
    // Treatment content
    treatmentData.medications.forEach((med, index) => {
      content.push({
        id: `treatment-med-${index}`,
        title: med.name,
        content: `${med.drugs.join(', ')}. Stage: ${med.stage}. Effectiveness: ${med.effectiveness}. Side Effects: ${med.sideEffects}`,
        category: 'Treatment',
        subcategory: 'Medications'
      });
    });
    
    treatmentData.nonPharmacological.forEach((category, index) => {
      category.approaches.forEach((approach, approachIndex) => {
        content.push({
          id: `treatment-nonpharm-${index}-${approachIndex}`,
          title: approach,
          content: `${category.category}: ${approach}`,
          category: 'Treatment',
          subcategory: 'Non-Pharmacological'
        });
      });
    });
    
    treatmentData.caregiving.forEach((guideline, index) => {
      content.push({
        id: `treatment-care-${index}`,
        title: 'Caregiving Guidelines',
        content: guideline,
        category: 'Treatment',
        subcategory: 'Caregiving'
      });
    });
    
    return content;
  }, []);

  // Filter content based on search term and category
  const filteredContent = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const searchQuery = searchTerm.toLowerCase();
    return searchableContent.filter(item => {
      const matchesSearch = 
        item.title.toLowerCase().includes(searchQuery) ||
        item.content.toLowerCase().includes(searchQuery) ||
        item.subcategory.toLowerCase().includes(searchQuery);
      
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory, searchableContent]);

  const categories = ['all', 'Overview', 'Diagnosis', 'Treatment'];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          Alzheimer's Disease Knowledge Base
        </Typography>
        <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Comprehensive information about Alzheimer's disease, diagnosis, and treatment
        </Typography>
        
        {/* Search Section */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search for symptoms, treatments, medications, stages, risk factors..."
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => setShowSearch(true)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'background.paper',
              }
            }}
          />
          
          {searchTerm && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon color="action" />
              <Typography variant="body2" color="text.secondary">
                Filter by category:
              </Typography>
              <ButtonGroup size="small" variant="outlined">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? 'contained' : 'outlined'}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category === 'all' ? 'All' : category}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          )}
        </Box>
        
        {/* Search Results */}
        <Collapse in={showSearch && searchTerm.trim() !== ''}>
          <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.accent' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Search Results
                <Badge 
                  badgeContent={filteredContent.length} 
                  color="primary" 
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowSearch(false)}
              >
                Hide Results
              </Button>
            </Box>
            
            {filteredContent.length > 0 ? (
              <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredContent.map((item) => (
                  <SearchResultItem
                    key={item.id}
                    item={item}
                    searchTerm={searchTerm}
                    onNavigate={() => navigateToContent(item.category)}
                  />
                ))}
              </Box>
            ) : (
              <Alert severity="info">
                <AlertTitle>No Results Found</AlertTitle>
                Try different keywords or check spelling. You can also browse by category using the tabs below.
              </Alert>
            )}
          </Paper>
        </Collapse>
      </Box>

      <Alert severity="info" sx={{ mb: 4 }}>
        <AlertTitle>Important Notice</AlertTitle>
        This information is for educational purposes only and should not replace professional medical advice. 
        Always consult with healthcare providers for personalized diagnosis and treatment.
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="knowledge base tabs">
          <Tab label="Overview" icon={<InfoIcon />} />
          <Tab label="Diagnosis" icon={<AssignmentIcon />} />
          <Tab label="Treatment" icon={<HealingIcon />} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="What is Alzheimer's Disease?"
                titleTypographyProps={{ variant: 'h5' }}
                avatar={<PsychologyIcon color="primary" />}
              />
              <CardContent>
                <Typography variant="body1" paragraph>
                  {overviewData.definition}
                </Typography>
                <Typography variant="h6" gutterBottom>
                  Key Statistics
                </Typography>
                <List>
                  {overviewData.statistics.map((stat, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <InfoIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={stat} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Stages of Alzheimer's Disease"
                titleTypographyProps={{ variant: 'h5' }}
                avatar={<TimelineIcon color="primary" />}
              />
              <CardContent>
                {overviewData.stages.map((stage, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">{stage.name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1" paragraph>
                        {stage.description}
                      </Typography>
                      <Chip 
                        label={`Duration: ${stage.duration}`} 
                        color="primary" 
                        variant="outlined" 
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="Early Warning Signs"
                titleTypographyProps={{ variant: 'h5' }}
                avatar={<WarningIcon color="warning" />}
              />
              <CardContent>
                <List>
                  {diagnosisData.earlySymptoms.map((symptom, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <WarningIcon color="warning" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={symptom} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="Risk Factors"
                titleTypographyProps={{ variant: 'h5' }}
                avatar={<InfoIcon color="info" />}
              />
              <CardContent>
                {diagnosisData.riskFactors.map((risk, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ mr: 1 }}>
                        {risk.factor}
                      </Typography>
                      <Chip 
                        label={risk.risk} 
                        color={risk.risk === 'High' ? 'error' : risk.risk === 'Medium' ? 'warning' : 'success'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {risk.description}
                    </Typography>
                    {index < diagnosisData.riskFactors.length - 1 && <Divider sx={{ mt: 1 }} />}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Diagnostic Tests"
                titleTypographyProps={{ variant: 'h5' }}
                avatar={<LocalHospitalIcon color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  {diagnosisData.diagnosticTests.map((test, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper elevation={1} sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          {test.name}
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {test.description}
                        </Typography>
                        <Chip 
                          label={`Accuracy: ${test.accuracy}`} 
                          color="primary" 
                          variant="outlined" 
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="FDA-Approved Medications"
                titleTypographyProps={{ variant: 'h5' }}
                avatar={<HealingIcon color="primary" />}
              />
              <CardContent>
                {treatmentData.medications.map((med, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">{med.name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Medications:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          {med.drugs.map((drug, drugIndex) => (
                            <Chip key={drugIndex} label={drug} color="primary" />
                          ))}
                        </Box>
                      </Box>
                      <Typography variant="body2" paragraph>
                        <strong>Stage:</strong> {med.stage}
                      </Typography>
                      <Typography variant="body2" paragraph>
                        <strong>Effectiveness:</strong> {med.effectiveness}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Side Effects:</strong> {med.sideEffects}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Non-Pharmacological Interventions"
                titleTypographyProps={{ variant: 'h5' }}
                avatar={<GroupIcon color="secondary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  {treatmentData.nonPharmacological.map((category, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper elevation={1} sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          {category.category}
                        </Typography>
                        <List dense>
                          {category.approaches.map((approach, approachIndex) => (
                            <ListItem key={approachIndex}>
                              <ListItemIcon>
                                <InfoIcon color="secondary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={approach} />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Caregiving Guidelines"
                titleTypographyProps={{ variant: 'h5' }}
                avatar={<GroupIcon color="primary" />}
              />
              <CardContent>
                <Grid container spacing={1}>
                  {treatmentData.caregiving.map((guideline, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                        <InfoIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2">{guideline}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Floating Action Button for Chat - Only show for authenticated users */}
      {isAuthenticated && (
        <Tooltip title="Ask AI Assistant" placement="left">
          <Fab
            color="primary"
            aria-label="chat"
            onClick={() => setChatBotOpen(true)}
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000,
              display: chatBotOpen ? 'none' : 'flex',
              background: `linear-gradient(135deg, ${theme => theme.palette.primary.main}, ${theme => theme.palette.secondary.main})`,
              '&:hover': {
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <ChatIcon />
          </Fab>
        </Tooltip>
      )}

      {/* ChatBot Component */}
      <ChatBot
        isOpen={chatBotOpen}
        onClose={() => setChatBotOpen(false)}
      />
    </Container>
  );
}

export default AlzheimerKnowledgeBase; 